import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { touchInteractiveSession } from "../lib/inactivity";
import { toast } from "sonner";
import { ArrowLeft, Mail, Lock, Eye, EyeOff } from "lucide-react";

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg || JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function SignIn() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const next = search.get("next") || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    // Password managers and some Android WebViews populate the DOM without
    // dispatching React's change event. Read the submitted controls as the
    // source of truth so a just-saved password can be used immediately.
    const values = new FormData(e.currentTarget);
    const submittedEmail = String(values.get("email") || email).trim().toLowerCase();
    const submittedPassword = String(values.get("password") || password);
    if (!submittedEmail || !submittedPassword) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { email: submittedEmail, password: submittedPassword });
      if (r.data?.session_token) localStorage.setItem("session_token", r.data.session_token);
      touchInteractiveSession();
      await refresh();
      toast.success(`Welcome back, ${r.data.user.name}`);
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 grid place-items-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-300">
          <ArrowLeft size={14}/> Back
        </Link>
        <div className="mt-6">
          <div className="label-eyebrow">Welcome back</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Sign in</h1>
          <p className="text-sm text-zinc-400 mt-2">Use your Proximity email and password.</p>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-3">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16}/>
            <input
              data-testid="signin-email"
              name="email" type="email" autoComplete="email" required
              autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-10 pr-3 py-3 text-sm focus:outline-none focus:border-[#007AFF]"
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16}/>
            <input
              data-testid="signin-password"
              name="password" type={show ? "text" : "password"} autoComplete="current-password" required
              autoCapitalize="none" autoCorrect="off" spellCheck={false}
              value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-10 pr-10 py-3 text-sm focus:outline-none focus:border-[#007AFF]"
            />
            <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
              {show ? <EyeOff size={14}/> : <Eye size={14}/>}
            </button>
          </div>
          <button
            data-testid="signin-submit"
            type="submit" disabled={busy}
            className="w-full bg-[#007AFF] hover:bg-[#005ec4] text-white font-bold py-3 rounded-full disabled:opacity-50"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <div className="text-right">
            <Link
              to="/forgot"
              data-testid="forgot-link"
              className="text-xs text-zinc-400 hover:text-[#007AFF] font-semibold"
            >
              Forgot password?
            </Link>
          </div>
        </form>

        <div className="mt-5 text-center text-sm text-zinc-400">
          New to Proximity?{" "}
          <Link
            to={`/signup${next !== "/dashboard" ? `?next=${encodeURIComponent(next)}` : ""}`}
            data-testid="goto-signup"
            className="text-[#007AFF] font-bold"
          >
            Create an account →
          </Link>
        </div>
      </div>
    </div>
  );
}
